/**
 * Unified Authentication Service for What To Eat?
 * Phase 2 cleanup: single authService.login/register/signOut for all roles (client/provider/admin).
 *
 * Relies on globals exposed by:
 *  - firebase-config.js           (window.firebaseConfig, legacy wrappers)
 *  - firebase-sdk-bootstrap.js    (window.firebaseAuthInstance, window.firebaseAuthRaw,
 *                                  window.firebaseDbInstance, window.firebaseReady)
 *  - firebase-integration.js      (window.firebaseSignIn, window.firebaseSignUp,
 *                                  window.firebaseSignOut, window.getCurrentUser, window.getUserRole)
 *
 * Load order: firebase-config.js → firebase-sdk-bootstrap.js → firebase-storage-integration.js
 *             → error-logger.js → firebase-integration.js → auth-service.js → app.js
 */

(function () {
  "use strict";

  // ============ READY WAIT ============
  function waitForFirebaseReady(timeoutMs) {
    const maxWait = timeoutMs || 15000;
    return new Promise((resolve) => {
      if (window.firebaseReady && window.firebaseAuthInstance) {
        return resolve(true);
      }
      const start = Date.now();
      const timer = setInterval(() => {
        if (Date.now() - start > maxWait) {
          clearInterval(timer);
          return resolve(false);
        }
        if (window.firebaseReady && window.firebaseAuthInstance) {
          clearInterval(timer);
          return resolve(true);
        }
        if (typeof window.ensureFirebaseReady === "function") {
          try { window.ensureFirebaseReady(); } catch (e) {}
        }
      }, 100);
    });
  }

  // ============ UUID GENERATION ============
  // Replaces all Date.now() IDs. Uses crypto.randomUUID v4 when available.
  function generateUUID() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    // Fallback: crypto.getRandomValues v4
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const bytes = crypto.getRandomValues(new Uint8Array(16));
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return (
        hex.slice(0, 8) + "-" +
        hex.slice(8, 12) + "-" +
        hex.slice(12, 16) + "-" +
        hex.slice(16, 20) + "-" +
        hex.slice(20)
      );
    }
    // Last-resort non-crypto fallback (still collision-resistant for client IDs)
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // ============ REGISTER ============
  // Creates a Firebase Auth user + Firestore doc via firebase-integration.js.
  function getPasswordStrengthError(password) {
    const value = String(password || "");
    if (value.length < 8) return "Password must be at least 8 characters.";
    if (!/[A-Z]/.test(value)) return "Password must include at least one uppercase letter.";
    if (!/[a-z]/.test(value)) return "Password must include at least one lowercase letter.";
    if (!/[0-9]/.test(value)) return "Password must include at least one number.";
    if (!/[^A-Za-z0-9]/.test(value)) return "Password must include at least one special character.";
    return "";
  }

  function getProductionSecurityState() {
    const hostname = String((window.location && window.location.hostname) || '').toLowerCase();
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
    const appCheckKey = String(
      (window.__APP_CHECK_SITE_KEY__ || (window.firebaseAppCheckConfig && window.firebaseAppCheckConfig.siteKey) || '')
    ).trim();
    const recaptchaKey = String(
      (window.__RECAPTCHA_SITE_KEY__ || (window.recaptchaConfig && window.recaptchaConfig.siteKey) || '')
    ).trim();
    const placeholderValues = new Set(['', 'replace-me', 'demo', 'example', 'test', 'invalid']);
    const hasAppCheck = appCheckKey.length > 0 && !placeholderValues.has(appCheckKey.toLowerCase());
    const hasRecaptcha = recaptchaKey.length > 0 && !placeholderValues.has(recaptchaKey.toLowerCase());
    return {
      hostname,
      isLocalhost,
      requiresProductionSecurity: !isLocalhost,
      hasAppCheck,
      hasRecaptcha,
      isReady: isLocalhost || (hasAppCheck && hasRecaptcha)
    };
  }

  function enforceProductionSecurity() {
    const securityState = getProductionSecurityState();
    if (!securityState.requiresProductionSecurity) {
      return;
    }
    if (!securityState.isReady) {
      throw new Error('Production security is not configured for this domain. Configure valid Firebase App Check and reCAPTCHA keys before allowing registration.');
    }
  }

  async function register(email, password, role, profile) {
    if (!(await waitForFirebaseReady())) {
      throw new Error("Firebase not ready. Please try again in a moment.");
    }
    enforceProductionSecurity();
    const passwordError = getPasswordStrengthError(password);
    if (passwordError) {
      throw new Error(passwordError);
    }
    if (typeof window.validateRealEmail === "function") {
      email = window.validateRealEmail(email);
    }
    if (typeof window.firebaseSignUp !== "function") {
      throw new Error("Firebase integration layer not loaded (firebaseSignUp missing).");
    }
    const user = await window.firebaseSignUp(email, password, role, profile || {});
    console.log("[authService] Registered user:", role, email, user && user.uid);
    return user;
  }

  // ============ AUTHORIZATION VALIDATION ============
  // validateProviderApproval — dual check: token claims AND Firestore doc.
  // The token claim `approved: true` is the primary gate (Firestore rules enforce it).
  // The Firestore doc check is a secondary defense-in-depth guard.
  function getProviderApprovalState(provider) {
    const data = provider || {};
    const accountStatus = String(data.accountStatus || "").toLowerCase();
    const verificationStatus = String(data.verificationStatus || "").toLowerCase();
    const subscriptionStatus = String(data.subscriptionStatus || "pending").toLowerCase();
    const isExplicitlyRejected = subscriptionStatus === "rejected" || accountStatus === "rejected" || verificationStatus === "rejected";
    const isExplicitlySuspended = subscriptionStatus === "suspended" || accountStatus === "suspended";
    const approved = (subscriptionStatus === "approved" || subscriptionStatus === "active" || accountStatus === "active") &&
      !isExplicitlyRejected &&
      !isExplicitlySuspended;
    return { approved, isExplicitlyRejected, isExplicitlySuspended };
  }

  async function findProviderByAuthenticatedIdentity(user, db) {
    if (!user || !db || typeof db.collection !== "function") {
      return null;
    }

    const email = String(user.email || "").trim().toLowerCase();
    const candidates = [];

    async function addQueryMatch(fieldName) {
      if (!email) return;
      const querySnap = await db.collection("providers").where(fieldName, "==", email).get();
      if (querySnap && typeof querySnap.forEach === "function") {
        querySnap.forEach((doc) => {
          const data = doc.data() || {};
          candidates.push({ id: doc.id, ...data });
        });
      }
    }

    const directSnap = await db.collection("providers").doc(user.uid).get();
    if (directSnap && directSnap.exists) {
      candidates.push({ id: directSnap.id || user.uid, ...(directSnap.data() || {}) });
    }

    await addQueryMatch("email");
    await addQueryMatch("providerEmail");
    await addQueryMatch("ownerEmail");

    const matchingProviders = candidates.filter((provider) => {
      const providerUid = String(provider.uid || provider.id || "").trim();
      const providerEmail = String(provider.email || provider.providerEmail || provider.ownerEmail || "").trim().toLowerCase();
      return providerUid === String(user.uid) || (email && providerEmail === email);
    });

    return matchingProviders.find((provider) => getProviderApprovalState(provider).approved) ||
      matchingProviders[0] ||
      null;
  }

  async function validateProviderApproval(user, candidateProvider) {
    const auth = window.firebaseAuthInstance;
    if (!user || !user.uid) {
      throw new Error("Provider login requires an authenticated Firebase user.");
    }

    // Primary: verify the backend-issued `approved` custom claim.
    let claimApproved = false;
    try {
      const idTokenResult = await user.getIdTokenResult(true);
      const claims = idTokenResult.claims || {};
      claimApproved = claims.approved === true && (claims.role === "provider" || !claims.role);
    } catch (e) {
      console.warn("[authService] validateProviderApproval: token refresh failed:", e.message);
    }

    // Check against the Firestore provider doc (fallback & cross-check).
    const db = window.firebaseDbInstance || window.firebaseDbRaw || null;
    let provider = candidateProvider || {};
    let docApproved = false;
    let isExplicitlyRejected = false;
    let isExplicitlySuspended = false;

    const candidateState = getProviderApprovalState(provider);
    docApproved = candidateState.approved;
    isExplicitlyRejected = candidateState.isExplicitlyRejected;
    isExplicitlySuspended = candidateState.isExplicitlySuspended;

    if (db && typeof db.collection === "function") {
      try {
        const matchedProvider = await findProviderByAuthenticatedIdentity(user, db);
        if (matchedProvider) {
          provider = matchedProvider;
          const state = getProviderApprovalState(provider);
          docApproved = state.approved;
          isExplicitlyRejected = state.isExplicitlyRejected;
          isExplicitlySuspended = state.isExplicitlySuspended;
        }
      } catch (e) {
        console.warn("[authService] validateProviderApproval: Firestore check error:", e.message);
      }
    }

    if (isExplicitlyRejected) {
      if (typeof auth.signOut === "function") {
        try { await auth.signOut(); } catch (signOutErr) {}
      }
      throw new Error("Your provider account registration was rejected. Please contact support.");
    }

    if (isExplicitlySuspended) {
      if (typeof auth.signOut === "function") {
        try { await auth.signOut(); } catch (signOutErr) {}
      }
      throw new Error("Your provider account has been suspended. Please contact support.");
    }

    if (!claimApproved && !docApproved) {
      if (typeof auth.signOut === "function") {
        try { await auth.signOut(); } catch (signOutErr) {}
      }
      throw new Error("Your provider account is pending Super Admin approval. You will be able to log in after approval.");
    }

    return provider;
  }

  // ============ LOGIN ============
  // Signs in with Firebase Auth once, detects role (admin/client/provider) by
  // reusing the authenticated user object — no second sign-in call is made.
  // Returns { role, user, data, claims, authenticatedVia }.
  async function login(email, password) {
    if (!(await waitForFirebaseReady())) {
      throw new Error("Firebase not ready. Please try again in a moment.");
    }
    const auth = window.firebaseAuthInstance;
    if (!auth || typeof auth.signInWithEmailAndPassword !== "function") {
      throw new Error("Firebase Auth not initialized.");
    }

    const userCredential = await auth.signInWithEmailAndPassword(email, password);
    const user = userCredential.user;
    const userEmail = (user.email || "").toLowerCase();

    // ============ ADMIN CHECK (backend-issued custom claims only) ============
    // Admin authorization is 100% backend-driven: only the `admin: true` (or
    // `role: admin`) custom claim issued by Firebase Auth / Admin SDK / Cloud
    // Functions grants access. No client-side email whitelist is used.
    try {
      const idTokenResult = await user.getIdTokenResult(true);
      const claims = idTokenResult.claims || {};
      const isAdminClaim = claims.admin === true || claims.role === "admin";

      if (isAdminClaim) {
        console.log("[authService] Admin login granted via backend custom claim:", userEmail);
        return { role: "admin", user, data: null, claims, authenticatedVia: "firebase-auth" };
      }
    } catch (e) {
      // Token refresh failed — fail closed (do NOT grant admin based on email).
      console.warn("[authService] Token refresh failed during admin check:", e.message);
    }

    // ============ CLIENT/PROVIDER ROLE DETECTION ============
    // Use the already-authenticated user object to detect role — avoids calling
    // signInWithEmailAndPassword a second time (which was the double sign-in bug).
    if (typeof window.firebaseSignInWithUser === "function") {
      try {
        const roleData = await window.firebaseSignInWithUser(user);
        if (roleData && roleData.role === "provider") {
          await validateProviderApproval(user, roleData.data);
        }
        return {
          role: roleData.role,
          user,
          data: roleData.data || null,
          claims: {},
          authenticatedVia: "firebase-integration"
        };
      } catch (e) {
        // Role detection failed (e.g., no client/provider doc found). Sign out so
        // the user isn't left authenticated with no session.
        console.warn("[authService] Role detection failed for", userEmail, ":", e.message);
        try { await auth.signOut(); } catch (signOutErr) {}
        throw e;
      }
    }

    // Legacy fallback: firebaseSignInWithUser not available, fall back to re-sign-in.
    // This path should not be reached in normal operation.
    if (typeof window.firebaseSignIn === "function") {
      try {
        const roleData = await window.firebaseSignIn(email, password);
        if (roleData && roleData.role === "provider") {
          await validateProviderApproval(user, roleData.data);
        }
        return {
          role: roleData.role,
          user,
          data: roleData.data || null,
          claims: {},
          authenticatedVia: "firebase-integration-legacy"
        };
      } catch (e) {
        console.warn("[authService] Legacy role detection failed for", userEmail, ":", e.message);
        try { await auth.signOut(); } catch (signOutErr) {}
        throw e;
      }
    }

    // Fallback: no integration layer — expose raw user with unknown role.
    return { role: null, user, data: null, claims: {}, authenticatedVia: "unknown" };
  }

  // ============ SIGN OUT ============
  async function signOut() {
    if (!(await waitForFirebaseReady())) return;
    if (typeof window.firebaseSignOut === "function") {
      await window.firebaseSignOut();
      return;
    }
    const auth = window.firebaseAuthInstance;
    if (auth && typeof auth.signOut === "function") {
      await auth.signOut();
    }
  }

  // ============ PASSWORD RESET ============
  // Firebase changes the password only after the account owner proves access
  // to the email address by opening its signed, single-use reset link.
  async function requestPasswordReset(email) {
    if (!(await waitForFirebaseReady())) {
      throw new Error("Firebase not ready. Please try again in a moment.");
    }

    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      throw new Error("Enter a valid email address.");
    }

    const auth = window.firebaseAuthInstance;
    if (!auth || typeof auth.sendPasswordResetEmail !== "function") {
      throw new Error("Firebase password reset is not available.");
    }

    await auth.sendPasswordResetEmail(normalizedEmail);
  }

  // ============ HELPERS ============
  function getCurrentUser() {
    const auth = window.firebaseAuthInstance;
    return auth ? auth.currentUser : null;
  }

  // Returns the role from the current user's token claims (server-issued).
  // Falling back to window.getUserRole() is intentionally removed — that function
  // reads the in-memory `userRole` variable which is set client-side and must not
  // be used as an authorization signal.
  async function getRole() {
    const auth = window.firebaseAuthInstance;
    const user = auth && auth.currentUser;
    if (!user) return null;
    try {
      const idTokenResult = await user.getIdTokenResult(false);
      const claims = idTokenResult.claims || {};
      if (claims.admin === true || claims.role === "admin") return "admin";
      if (claims.role === "provider" && claims.approved === true) return "provider";
      if (claims.role === "client") return "client";
    } catch (e) {
      console.warn("[authService] getRole: token read failed:", e.message);
    }
    return null;
  }

  // Synchronous role peek from cached token — use only for non-security UI hints.
  function getRoleSync() {
    if (typeof window.getUserRole === "function") {
      return window.getUserRole();
    }
    return null;
  }

  function onAuthStateChanged(cb) {
    const auth = window.firebaseAuthInstance;
    if (!auth || typeof auth.onAuthStateChanged !== "function") {
      console.warn("[authService] onAuthStateChanged called before Firebase Auth ready");
      return () => {};
    }
    return auth.onAuthStateChanged(cb);
  }

  // ============ EXPOSE GLOBALS ============
  window.authService = {
    register: register,
    login: login,
    signOut: signOut,
    requestPasswordReset: requestPasswordReset,
    getCurrentUser: getCurrentUser,
    getRole: getRole,
    getRoleSync: getRoleSync,
    onAuthStateChanged: onAuthStateChanged,
    waitForFirebaseReady: waitForFirebaseReady
  };

  window.generateUUID = generateUUID;
  window.uuidv4 = generateUUID;

  console.log("[authService] Unified auth service loaded (login/register/signOut/password reset + generateUUID)");
})();
